import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { IDepartment } from '@boma/administration-portal/shared/entities';
import { IUser } from '@boma/client-shared';
import { IBrand } from '@boma/shared/entities';
import {
  DefinitionTarget,
  ICategory,
  ICustomer,
  IDefinition,
  IDefinitionsDataResponse,
  IDefinitionSingleDefinitionTargetResponse,
  IFilterBlockOption,
  IFilterSearch,
  IGlobalFilters,
  ILocation,
  ILocationSimple,
  IProduct,
  IProfileEntity,
} from '@boma/supplier-portal/shared/entities';
import { DefinitionsService } from '@boma/supplier-portal/shared/services';
import { getSelectedOrganizationInfo } from '@boma/supplier-portal/shared/state';
import { Store } from '@ngrx/store';
import { ConfirmationService, MessageService } from 'primeng/api';
import { take } from 'rxjs/operators';

@Component({
  selector: 'boma-filter-block',
  templateUrl: './filter-block.component.html',
  styleUrls: ['./filter-block.component.scss'],
})
export class FilterBlockComponent implements OnInit {
  @Input() displayOrder: number[] = [];

  // Simply pass values into this if you want the block to appear
  @Input() searchBlock: { placeHolder: string } = null;
  @Input() locationBlock: IFilterBlockOption = null;
  @Input() productBlock: IFilterBlockOption = null;
  @Input() customerBlock: IFilterBlockOption = null;
  @Input() userBlock: IFilterBlockOption = null;
  @Input() confirmation = 'Are you sure you want to start a new search?';
  @Input() dateBlock: { singleDay: boolean; shortcut: string } = null;
  @Input() returnValue = 'results';
  @Input() searching = false;
  @Input() customActions; // Populated in the dialogue, from new search button.
  @Input() newSearch;
  @Input() confirmSearch = true;
  @Input() showLabels = false;
  @Input() showHelp = false;
  @Output() searchHandler: EventEmitter<IFilterSearch> = new EventEmitter();
  @Output() definitionResults: EventEmitter<IDefinitionsDataResponse> =
    new EventEmitter();

  searched = false;

  currentOrganization: IProfileEntity;

  showLocationDefinitions = false;
  showCustomerDefinitions = false;
  showProductDefinitions = false;
  dateString: string;
  selectedDates: { fromDate?: string; toDate?: string; dateString?: string };

  locationDefinitions = [];
  productDefinitions = [];
  customerDefinitions = [];

  resultsLoaded = false;

  alert = false;

  //Search Parameters
  searchQuery: string = null;
  selectedLocations: ILocation[] = null;
  selectedDefinitions: IDefinition[] = null;
  selectedCustomers: ICustomer[] = null;
  selectedUsers: IUser[] = null;
  selectedProducts: IProduct[] = null;

  selectedDepartments: IDepartment[] = null;
  selectedCategories: ICategory[] = null;
  selectedBrands: IBrand[] = null;

  customerLocations = false;
  defintionSearchResults: IDefinitionsDataResponse;

  constructor(
    private store: Store,
    private definitionsService: DefinitionsService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService
  ) {}

  ngOnInit(): void {
    this.store
      .select(getSelectedOrganizationInfo)
      .pipe(take(1))
      .subscribe((org) => {
        this.currentOrganization = org;
      });
  }

  // Piping these through to the component with the crud table
  handleSelectedLocations(locations) {
    this.selectedLocations = locations;
    if (!this.productBlock && this.locationBlock && !this.customerBlock)
      this.definitionResults.emit({
        locations: this.selectedLocations as ILocationSimple[],
        customers: [],
        products: [],
        users: [],
      });
  }

  handleSelectedCustomers(customers) {
    console.log(customers);
    this.selectedCustomers = customers;
  }

  handleSelectedUser(user) {
    this.selectedUsers = [user.user];
  }

  handleProductsSelection(products) {
    console.log(products);
    this.selectedProducts = products;
  }

  handleSelectedDefinitions(
    target: IDefinitionSingleDefinitionTargetResponse,
    type: number
  ) {
    if (type === DefinitionTarget.Location) {
      this.locationDefinitions = target.definitions;
    } else if (type === DefinitionTarget.Product) {
      this.productDefinitions = target.definitions;
    } else if (type === DefinitionTarget.Customer) {
      this.customerDefinitions = target.definitions;
    }
  }

  handleSelectedBrands(ev) {
    console.log(ev);
    this.selectedBrands = ev;
  }

  handleSelectedDepartments(ev) {
    console.log(ev);
    this.selectedDepartments = [ev];
  }

  handleSelectedCategories(ev) {
    this.selectedCategories = ev;
    console.log(ev);
  }

  handleSelectedDate(ev) {
    // TODO pass through friendly date range name from component
    if (ev[2] !== '') {
      this.dateString = ev[2];
    } else {
      this.dateString = 'From: ' + ev[0] + ' To: ' + ev[1];
    }

    this.selectedDates = {
      fromDate: ev[0],
      toDate: ev[1],
      dateString: this.dateString,
    };
  }

  alertAction(action) {
    this.alert = false;
    action();
  }

  alertAccept() {
    if (this.newSearch) this.newSearch(); // using this to clear results from parent component
    this.searched = false;
    this.alert = false;
    this.getDefinitionDetails();
  }

  alertReject() {
    this.alert = false;
  }

  getDefinitionDetails() {
    this.searched = true;
    // this.resultsLoaded = false; // PASS THROUGH THE RESULT RECEIVED LATER FOR NICER UX

    const selectedDefinitionIds = [];
    const selectedCustomerDefinitionIds = [];
    const selectedLocationDefinitionIds = [];
    const selectedProductDefinitionIds = [];
    let targetId = 0;
    let definitionFilters = null;

    this.productDefinitions
      .filter((x) => {
        return x !== null;
      })
      .map((p) => {
        console.log(p);
        selectedDefinitionIds.push(p.id);
        selectedProductDefinitionIds.push(p.id);
      });

    this.customerDefinitions
      .filter((x) => {
        return x !== null;
      })
      .map((c) => {
        selectedDefinitionIds.push(c.id);
        selectedCustomerDefinitionIds.push(c.id);
      });

    this.locationDefinitions
      .filter((x) => {
        return x !== null;
      })
      .map((l) => {
        selectedDefinitionIds.push(l.id);
        selectedLocationDefinitionIds.push(l.id);
      });

    if (selectedCustomerDefinitionIds.length) targetId += 1;
    if (selectedProductDefinitionIds.length) targetId += 2;
    if (selectedLocationDefinitionIds.length) targetId += 4;

    if (targetId !== 0) {
      definitionFilters = {
        targets: targetId,
        definitionIds: selectedDefinitionIds,
        customerIds: selectedCustomerDefinitionIds,
        productIds: selectedProductDefinitionIds,
        locationIds: selectedLocationDefinitionIds,
      };
    } else {
      //TODO this should probably be rectified on the API
      definitionFilters = {
        targets: 1,
        definitionIds: [],
      };
    }
    //Save the query and match with a new query so we dont run same query twice.
    if (this.returnValue === 'results') {
      console.log('returning results');
      this.definitionsService
        .getDataForDefinitions(this.currentOrganization.id, {
          definitionsFilters: definitionFilters,
          brandIds: this.selectedBrands?.map((b) => b.id),
          categoryIds: this.selectedCategories?.map((c) => c.id),
          departmentIds: this.selectedDepartments?.map((d) => d.id),
          locationIds: this.selectedLocations?.map((l) => l.id),
          productIds: this.selectedProducts?.map((p) => p.id),
          customerIds: this.selectedCustomers?.map((customer) => customer.id),
        } as IGlobalFilters)
        .subscribe(
          (x) => {
            console.log('definition results', x);
            x.users = this.selectedUsers;
            this.definitionResults.emit(x);
          },
          (error) => {
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail:
                'There was a problem retrieving your search results, please try again.',
            });
          }
        );
    } else if (this.returnValue === 'filters') {
      this.searchHandler.emit({
        query: this.searchQuery,
        products: this.selectedProducts,
        definitions: selectedDefinitionIds, // TODO check if used remove if not used
        users: this.selectedUsers,
        locations: this.selectedLocations,
        customers: this.selectedCustomers,
        departments: this.selectedDepartments,
        categories: this.selectedCategories,
        brands: this.selectedBrands,
        definitionsFilters: definitionFilters,
        selectedDates: this.selectedDates,
      });
    }

    //  End of getDefinitionDetails
  }

  locationHandler(event) {
    this.selectedLocations = event;
  }
}
